import React, { useRef } from "react";
import { useDocusealEmbed } from "@/hooks/use-docuseal-embed";

/**
 * Mirrors the BuilderEmbed type from the API.
 * Keep in sync with api/src/services/pdf-form/vendor/pdf-fill-vendor.ts.
 */
export type BuilderEmbed = {
  kind: "docuseal-builder";
  token: string;
  scriptUrl: string;
};

interface PdfBuilderEmbedProps {
  embed: BuilderEmbed;
}

function DocuSealBuilder({
  token,
  scriptUrl,
}: {
  token: string;
  scriptUrl: string;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useDocusealEmbed({
    wrapperRef,
    elementTag: "docuseal-builder",
    scriptUrl,
    resetKey: token,
  });

  // We deliberately do NOT listen for the builder's `save`/`change` events —
  // they fire continuously on every edit. Importing is triggered only by the
  // explicit "Done" button in the parent dialog's footer.
  //
  // Builder is stripped to just field placement + naming:
  // - data-extract-fields=false: open the PDF clean (no auto-detected fields)
  // - data-with-field-placeholder=true: show the field-name box (prompt naming)
  // - hide Send / Sign Yourself: those actions are driven by our app
  // - data-roles="Candidate,Organization": the two signing parties. Candidate
  //   is first so new fields default to the candidate (the common case); the org
  //   moves offer-letter fields to "Organization". Signing ORDER is separate
  //   (form_file_template.signingOrder), not the builder role order.
  // - data-with-title=false: hide the document title
  // - data-with-upload-button=false: hide "Add document"
  // - data-with-documents-list=false: hide the documents sidebar (+ its remove)
  // No fixed height: the shadow-CSS injection above lets the builder grow to its
  // content height so the parent overflow-auto container scrolls the whole thing.
  return (
    <div className="w-full" ref={wrapperRef}>
      {React.createElement("docuseal-builder", {
        "data-token": token,
        "data-extract-fields": "false",
        "data-with-field-placeholder": "true",
        "data-with-send-button": "false",
        "data-with-sign-yourself-button": "false",
        "data-with-title": "false",
        "data-with-upload-button": "false",
        "data-with-documents-list": "false",
        "data-roles": "Candidate,Organization",
        style: { display: "block" },
      })}
    </div>
  );
}

// ─── Public component ───────────────────────────────────────────────────────

/** PDF field-mapping builder embed (DocuSeal web component). */
export function PdfBuilderEmbed({ embed }: PdfBuilderEmbedProps) {
  return <DocuSealBuilder token={embed.token} scriptUrl={embed.scriptUrl} />;
}
